import { isPhoneWidth } from './phone';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useAgents, useExperience, type ChatContext } from '@/api/hooks';
import type { Me } from '@/api/session';
import { useWords } from './technical';

/**
 * What the frame around every page holds (the prototype's header, P:80-168):
 * whether the rail is shown, whether the side chat is open and what it
 * should start with, whether the calendar is over the page, and the page's
 * own kicker and title — which the page names and the frame draws, so every
 * screen has the same header in the same place.
 */
export interface PageHead {
  kicker: string;
  title: string;
  /** Where ← goes. Today unless the page says otherwise; none on Today. */
  back?: { to: string; label: string } | null | undefined;
}

/**
 * The prototype's rulers (P:1692-1714), read from the window as it is now:
 * a phone is under 760px and gets one column — the rail as a drawer, the
 * chat as a full-screen sheet; the chat is 320px under 1180px and 360px
 * above until the person drags it, and then the width they chose, within
 * what leaves the page room (outreach A-17). The header's labels are measured against the column the header
 * lives in, not the window: words above 800px of column, icons above 470px,
 * and only the calendar below that.
 */
export interface Layout {
  phone: boolean;
  chatWidth: number;
  /** The header's action labels fit. */
  wide: boolean;
  /** Add and find-a-time fit beside the calendar; the back button keeps its word. */
  roomy: boolean;
}

interface Frame {
  /** A reused business page renders its heading inside Today's work panel. */
  inlineHead?: boolean;
  workspaceOpener: ((path: string, department?: string) => boolean) | null;
  setWorkspaceOpener: (opener: ((path: string, department?: string) => boolean) | null) => void;
  workspaceContext: ChatContext | null;
  setWorkspaceContext: (context: ChatContext | null) => void;
  layout: Layout;
  /** On a phone, the rail slides over the page. */
  drawerOpen: boolean;
  setDrawerOpen: (open: boolean) => void;
  head: PageHead | null;
  setHead: (head: PageHead | null) => void;
  railHidden: boolean;
  setRailHidden: (hidden: boolean) => void;
  chatOpen: boolean;
  setChatOpen: (open: boolean) => void;
  /** The width the person dragged the chat to, kept in this browser; null goes back to the default. */
  setChatWidth: (width: number | null) => void;
  /** Words the person starts from; they edit them and they send them. */
  draft: { text: string; at: number } | null;
  /**
   * Open the chat, optionally starting from words and naming what it is
   * about. The subject is identifiers — a task, a thread, a meeting — and
   * never text; the contract refuses anything else.
   */
  ask: (start?: string, about?: ChatContext) => void;
  /** A subject the person attached by asking about something; cleared with ×. */
  attached: ChatContext | null;
  detach: () => void;
  calendar: 'closed' | 'week' | 'find';
  setCalendar: (calendar: 'closed' | 'week' | 'find') => void;
  /** Whether the Permissions strip is drawn over the page (S:22); a lead's and a superadmin's. */
  permissionsOpen: boolean;
  setPermissionsOpen: (open: boolean) => void;
  /** The day Today works on, when the calendar picked one (P:2515-2532). */
  workingDay: { day: Date; at: number } | null;
  workOn: (day: Date) => void;
  /** The person's own twin, by its given name; null until known, or if they have none. */
  twin: string | null;
  /** "Rows per page" (Settings): how many rows a list shows before "Show more". */
  pageSize: number;
}

const Ctx = createContext<Frame | null>(null);

const RAIL_KEY = 'norum-rail-hidden';
const CHAT_WIDTH_KEY = 'norum-chat-width';
/** Narrower than this and a message line is a few words; wider leaves the page too little. */
export const CHAT_MIN = 280;
export const CHAT_MAX = 640;

function readChatWidth(): number | null {
  try {
    const stored = Number(localStorage.getItem(CHAT_WIDTH_KEY));
    return Number.isFinite(stored) && stored >= CHAT_MIN && stored <= CHAT_MAX ? stored : null;
  } catch {
    return null;
  }
}

function windowWidth(): number {
  return typeof window === 'undefined' ? 1280 : window.innerWidth;
}

/** A person who never chose opens with the rail as the prototype does: shown from 1100px. */
function readRailHidden(): boolean {
  try {
    const stored = localStorage.getItem(RAIL_KEY);
    if (stored === '1') return true;
    if (stored === '0') return false;
  } catch {
    // No memory: fall through to the width.
  }
  return windowWidth() < 1100;
}

function useWindowWidth(): number {
  const [width, setWidth] = useState(windowWidth);
  useEffect(() => {
    const onResize = () => setWidth(windowWidth());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return width;
}

export function FrameProvider({ me, children }: { me: Me; children: ReactNode }) {
  const agents = useAgents();
  const saved = Number(useExperience().data?.resolved.preferences['pageSize']);
  const pageSize = [10, 25, 50].includes(saved) ? saved : 25;
  const twin =
    agents.data?.agents.find((a) => a.agentKind === 'twin' && a.principal?.id === me.humanId)?.givenName ?? null;
  const width = useWindowWidth();
  const [workspaceContext, setWorkspaceContext] = useState<ChatContext | null>(null);
  const [workspaceOpener, setWorkspaceOpenerState] = useState<Frame['workspaceOpener']>(null);
  const setWorkspaceOpener = useCallback((opener: Frame['workspaceOpener']) => setWorkspaceOpenerState(() => opener), []);
  const [head, setHead] = useState<PageHead | null>(null);
  const [railHidden, setRailHiddenState] = useState(readRailHidden);
  const [drawerOpen, setDrawerOpen] = useState(false);
  // The chat opens by itself only where there is room beside the page (P:1386).
  const [chatOpen, setChatOpen] = useState(() => windowWidth() >= 1000);
  const [chatChosen, setChatChosen] = useState(readChatWidth);
  const setChatWidth = useCallback((next: number | null) => {
    const kept = next === null ? null : Math.round(Math.min(CHAT_MAX, Math.max(CHAT_MIN, next)));
    setChatChosen(kept);
    try {
      if (kept === null) localStorage.removeItem(CHAT_WIDTH_KEY);
      else localStorage.setItem(CHAT_WIDTH_KEY, String(kept));
    } catch {
      // Remembering is a convenience; the chat still takes the width.
    }
  }, []);
  const [draft, setDraft] = useState<Frame['draft']>(null);
  const [attached, setAttached] = useState<ChatContext | null>(null);
  const [calendar, setCalendar] = useState<Frame['calendar']>('closed');
  const [permissionsOpen, setPermissionsOpen] = useState(false);
  const [workingDay, setWorkingDay] = useState<Frame['workingDay']>(null);
  const workOn = useCallback((day: Date) => {
    setWorkingDay({ day, at: Date.now() });
    setCalendar('closed');
  }, []);

  const setRailHidden = useCallback((hidden: boolean) => {
    setRailHiddenState(hidden);
    try {
      localStorage.setItem(RAIL_KEY, hidden ? '1' : '0');
    } catch {
      // Remembering is a convenience; the rail still hides.
    }
  }, []);

  const ask = useCallback((start?: string, about?: ChatContext) => {
    setChatOpen(true);
    setDraft({ text: start ?? '', at: Date.now() });
    if (about) setAttached(about);
  }, []);
  const detach = useCallback(() => setAttached(null), []);

  const phone = isPhoneWidth(width);
  // The chosen width, but never more than leaves the page 40% of the window.
  const chatWidth = Math.min(chatChosen ?? (width < 1180 ? 320 : 360), Math.max(CHAT_MIN, Math.floor(width * 0.6) - 216));
  const mainWidth = width - (phone || railHidden ? 0 : 216) - (phone || !chatOpen ? 0 : chatWidth);
  const layout = useMemo<Layout>(
    () => ({ phone, chatWidth, wide: mainWidth >= 800, roomy: mainWidth >= 470 }),
    [phone, chatWidth, mainWidth],
  );

  const value = useMemo<Frame>(
    () => ({
      workspaceContext, setWorkspaceContext, workspaceOpener, setWorkspaceOpener,
      layout,
      drawerOpen,
      setDrawerOpen,
      head,
      setHead,
      railHidden,
      setRailHidden,
      chatOpen,
      setChatOpen,
      setChatWidth,
      draft,
      ask,
      attached,
      detach,
      calendar,
      setCalendar,
      permissionsOpen,
      setPermissionsOpen,
      workingDay,
      workOn,
      twin,
      pageSize,
    }),
    [workspaceContext, workspaceOpener, setWorkspaceOpener, layout, drawerOpen, head, railHidden, setRailHidden, chatOpen, setChatWidth, draft, ask, attached, detach, calendar, permissionsOpen, workingDay, workOn, twin, pageSize],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useFrame(): Frame {
  const frame = useContext(Ctx);
  if (!frame) throw new Error('useFrame outside the signed-in frame: render inside <Shell>.');
  return frame;
}

/**
 * A page names itself; the frame's header draws it. Renders nothing where
 * it stands.
 */
export function PageHeader({ kicker: said, title, back }: PageHead) {
  const frame = useContext(Ctx);
  const kicker = useWords()(said);
  const setHead = frame?.setHead;
  const backTo = back === null ? null : back?.to;
  const backLabel = back === null ? null : back?.label;
  useLayoutEffect(() => {
    if (!setHead) return;
    setHead({
      kicker,
      title,
      back: backTo === null ? null : backTo !== undefined ? { to: backTo, label: backLabel ?? '' } : undefined,
    });
  }, [setHead, kicker, title, backTo, backLabel]);
  useLayoutEffect(() => () => setHead?.(null), [setHead]);
  // Outside the frame (a unit test rendering one screen), say it in place.
  if (!frame || frame.inlineHead) {
    const Heading = frame ? 'h2' : 'h1';
    return (
      <header className="flex flex-col gap-0.5">
        <span className="text-micro uppercase tracking-label text-meta">{kicker}</span>
        <Heading className="m-0 text-readback font-semibold">{title}</Heading>
      </header>
    );
  }
  return null;
}

/** For a part that also renders on its own (a unit test of the rail). */
export function useFrameMaybe(): Frame | null {
  return useContext(Ctx);
}

/** Embedded records keep the company's header, while retaining chat/calendar actions. */
export function RecordPanelFrame({children}:{children:ReactNode}) {
 const frame=useContext(Ctx);
 const value=useMemo(()=>frame?{...frame,setHead:()=>undefined}:null,[frame]);
 return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/** Embedded pages keep their own title inside the work panel; they cannot
 * replace Today's global header or the active tab's assistant context. */
export function EmbeddedFrame({children}: {children: ReactNode}) {
  const frame=useFrame();
  const value=useMemo(()=>({...frame,inlineHead:true,setHead:()=>undefined}),[frame]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
